import React from 'react';

// Sidebar controls for local radial masks (Lightroom-style local adjustments).
// Geometry is edited on-canvas via MaskBox; the local tone/color tweaks live
// here. Everything is non-destructive and blended by the mask's feathered alpha.
const ADJ = [
  { key: 'exposure', label: 'Exposure', min: -2, max: 2, step: 0.05, fmt: (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)} EV` },
  { key: 'contrast', label: 'Contrast', min: -50, max: 50, step: 1 },
  { key: 'black', label: 'Lift blacks', min: 0, max: 60, step: 1 },
  { key: 'saturation', label: 'Saturation', min: -50, max: 60, step: 1 },
  { key: 'temp', label: 'Warmth', min: -50, max: 50, step: 1 },
  { key: 'tint', label: 'Tint', min: -50, max: 50, step: 1 },
];
const GEOM = [
  { key: 'feather', label: 'Feather', min: 0.05, max: 1, step: 0.01, def: 0.6, fmt: (v) => `${Math.round(v * 100)}%` },
  { key: 'amount', label: 'Strength', min: 0, max: 1, step: 0.01, def: 1, fmt: (v) => `${Math.round(v * 100)}%` },
];

function Slider({ label, value, min, max, step, fmt, onChange }) {
  return (
    <label className="cs-mask-slider">
      <span className="cs-mask-slider-label">{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="cs-mask-slider-val">{fmt ? fmt(value) : value}</span>
    </label>
  );
}

const RANGE = [
  { key: 'lo', label: 'Range low', min: 0, max: 1, step: 0.01, fmt: (v) => `${Math.round(v * 100)}%` },
  { key: 'hi', label: 'Range high', min: 0, max: 1, step: 0.01, fmt: (v) => `${Math.round(v * 100)}%` },
  { key: 'feather', label: 'Range feather', min: 0.01, max: 0.5, step: 0.01, fmt: (v) => `${Math.round(v * 100)}%` },
];

const chipLabel = (m, i) => {
  if (m.ai === 'sky') return `☁ Sky ${i + 1}`;
  if (m.ai === 'subject') return `◉ Subject ${i + 1}`;
  return m.type === 'linear' ? `▤ Gradient ${i + 1}` : `◎ Radial ${i + 1}`;
};

const pct = (v) => `${Math.round(v * 100)}%`;

export default function MaskPanel({
  masks, activeId, maskMode, onToggleMode, onAdd, onAddLinear, onAddAi,
  onSelect, onDelete, onUpdate, onUpdateAdj, busyAi, onSnapSubject, showPosition,
}) {
  const active = masks.find((m) => m.id === activeId) || null;
  const range = (active && active.range) || {};
  const isLinear = active && active.type === 'linear';
  return (
    <div className="cs-mask-panel">
      <div className="cs-mask-head">
        <div className="cs-scopes-label" style={{ margin: 0 }}>Local masks</div>
        <span style={{ flex: 1 }} />
        {onToggleMode && (
          <button
            className={`cs-btn cs-btn-sm ${maskMode ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
            onClick={onToggleMode}
            title="Show the mask handles on the image to position/resize them"
          >
            {maskMode ? 'Editing on image' : 'Edit on image'}
          </button>
        )}
      </div>
      <div className="cs-mask-addrow">
        <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={onAdd} title="Radial mask — dodge/burn, glow, local color">
          + Radial
        </button>
        <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={onAddLinear} title="Linear gradient mask — graduated sky/water, edge falloff">
          + Gradient
        </button>
        {onAddAi && (
          <>
            <button
              className="cs-btn cs-btn-sm cs-btn-ghost"
              onClick={() => onAddAi('sky')}
              disabled={busyAi}
              title="AI sky mask — a top-down gradient gated to bright sky luminance"
            >
              {busyAi === 'sky' ? '…' : '☁ Sky'}
            </button>
            <button
              className="cs-btn cs-btn-sm cs-btn-ghost"
              onClick={() => onAddAi('subject')}
              disabled={busyAi}
              title="AI subject mask — detects the main subject and centers a radial mask on it"
            >
              {busyAi === 'subject' ? '…' : '◉ Subject'}
            </button>
          </>
        )}
        {onSnapSubject && (
          <button
            className="cs-btn cs-btn-sm cs-btn-ghost"
            onClick={() => active && onSnapSubject(active.id)}
            disabled={!active}
            title="Move this mask onto the tracked subject at the current frame"
          >
            ✦ Follow subject
          </button>
        )}
      </div>

      {!masks.length ? (
        <div className="cs-help" style={{ padding: '4px 2px 2px' }}>
          Add a mask to brighten/darken or shift color in just one area — a radial
          for a barrel, a gradient for the sky/water, or let AI find the sky/subject.
          Add a luminance range to key it to highlights or shadows. Non-destructive,
          exported faithfully.
        </div>
      ) : (
        <div className="cs-mask-list">
          {masks.map((m, i) => (
            <button
              key={m.id}
              className={`cs-mask-chip${m.id === activeId ? ' on' : ''}`}
              onClick={() => onSelect(m.id)}
              title="Select to edit"
            >
              {chipLabel(m, i)}
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="cs-mask-edit">
          {showPosition && (
            <>
              <Slider label="Center X" value={Number(active.cx) || 0} min={0} max={1} step={0.005} fmt={pct} onChange={(v) => onUpdate(active.id, { cx: v })} />
              <Slider label="Center Y" value={Number(active.cy) || 0} min={0} max={1} step={0.005} fmt={pct} onChange={(v) => onUpdate(active.id, { cy: v })} />
              {isLinear ? (
                <Slider label="Angle" value={Number(active.angle) || 0} min={-180} max={180} step={1} fmt={(v) => `${Math.round(v)}°`} onChange={(v) => onUpdate(active.id, { angle: v })} />
              ) : (
                <>
                  <Slider label="Width" value={active.rx == null ? 0.3 : Number(active.rx)} min={0.03} max={0.95} step={0.01} fmt={pct} onChange={(v) => onUpdate(active.id, { rx: v })} />
                  <Slider label="Height" value={active.ry == null ? 0.3 : Number(active.ry)} min={0.03} max={0.95} step={0.01} fmt={pct} onChange={(v) => onUpdate(active.id, { ry: v })} />
                </>
              )}
              <div className="cs-mask-divider" />
            </>
          )}
          {ADJ.map((a) => (
            <Slider
              key={a.key}
              label={a.label}
              value={Number(active.adj?.[a.key]) || 0}
              min={a.min}
              max={a.max}
              step={a.step}
              fmt={a.fmt}
              onChange={(v) => onUpdateAdj(active.id, a.key, v)}
            />
          ))}
          <div className="cs-mask-divider" />
          {GEOM.map((g) => (
            <Slider
              key={g.key}
              label={g.key === 'feather' && isLinear ? 'Transition' : g.label}
              value={active[g.key] == null ? g.def : Number(active[g.key])}
              min={g.min}
              max={g.max}
              step={g.step}
              fmt={g.fmt}
              onChange={(v) => onUpdate(active.id, { [g.key]: v })}
            />
          ))}
          <div className="cs-mask-row">
            <label className="cs-mask-check">
              <input type="checkbox" checked={!!active.invert} onChange={(e) => onUpdate(active.id, { invert: e.target.checked })} />
              Invert ({isLinear ? 'flip direction' : 'affect outside'})
            </label>
          </div>
          <div className="cs-mask-divider" />
          <label className="cs-mask-check">
            <input
              type="checkbox"
              checked={!!range.enabled}
              onChange={(e) => onUpdate(active.id, { range: { ...range, enabled: e.target.checked } })}
            />
            Luminance range (key to a brightness band)
          </label>
          {range.enabled && RANGE.map((r) => (
            <Slider
              key={r.key}
              label={r.label}
              value={range[r.key] == null ? (r.key === 'hi' ? 1 : r.key === 'feather' ? 0.15 : 0) : Number(range[r.key])}
              min={r.min}
              max={r.max}
              step={r.step}
              fmt={r.fmt}
              onChange={(v) => onUpdate(active.id, { range: { ...range, [r.key]: v } })}
            />
          ))}
          <div className="cs-mask-row">
            <span style={{ flex: 1 }} />
            <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => onDelete(active.id)}>Delete mask</button>
          </div>
        </div>
      )}
    </div>
  );
}
